import { createLogger } from '@/utils/logger';

/**
 * FLOWING SKY BACKDROP for the About overlay (2026-09-02, user: "on the About's background I
 * want a sky-type shader effect — more immersive").
 *
 * A dedicated, self-contained WebGL canvas — completely separate from the main three.js scene —
 * painting slow domain-warped dusk veils in the brand palette (deep umber → terracotta, amber
 * light caught in the fold crests). It renders TRANSLUCENTLY (premultiplied alpha) so the
 * heavily-blurred 3D scene still glows through beneath it: blur + living sky together.
 *
 * Cost discipline: the backing store runs at HALF the CSS size (the soft look wants no more),
 * the fbm is 4 octaves of 2D value noise, and the rAF loop only runs while the About is OPEN —
 * closed, this class costs nothing.
 *
 * NO BACKTICKS inside the GLSL template literals (see the storyGrade gotcha).
 */
const VERT = /* glsl */ `
  attribute vec2 aPos;
  void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  precision mediump float;
  uniform vec2 uRes;
  uniform float uTime;

  float skyHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
  float skyNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(skyHash(i), skyHash(i + vec2(1.0, 0.0)), u.x),
      mix(skyHash(i + vec2(0.0, 1.0)), skyHash(i + vec2(1.0, 1.0)), u.x),
      u.y);
  }
  float skyFbm(vec2 p) {
    float s = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      s += a * skyNoise(p);
      p = p * 2.11 + vec2(5.2, 1.3);
      a *= 0.5;
    }
    return s;
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / uRes;
    vec2 p = vec2(uv.x * uRes.x / uRes.y, uv.y) * 2.6;
    float t = uTime * 0.05;

    /* Domain-warped veils - the same fluid language as the story sky, in dusk colours. */
    vec2 w = vec2(
      skyFbm(p * 0.8 + vec2(t * 0.7, -t * 0.4)),
      skyFbm(p * 0.8 + vec2(-t * 0.5, t * 0.6) + 7.3));
    float n = skyFbm(p + (w - 0.5) * 1.9 + vec2(t, t * 0.3));
    float ridge = pow(1.0 - abs(n * 2.0 - 1.0), 3.0);

    vec3 deep = vec3(0.10, 0.06, 0.04);  /* deep umber dusk */
    vec3 warm = vec3(0.62, 0.32, 0.14);  /* terracotta     */
    vec3 gold = vec3(1.00, 0.78, 0.42);  /* amber crest    */
    vec3 col = mix(deep, warm, smoothstep(0.32, 0.92, n));
    col += gold * ridge * ridge * 0.55;

    float a = 0.28 + 0.5 * smoothstep(0.3, 0.95, n) + ridge * 0.25;
    /* Airier centre band so the mark and the credits always read over it. */
    a *= 0.6 + 0.4 * smoothstep(0.12, 0.5, abs(uv.y - 0.52));
    a = clamp(a, 0.0, 0.9);

    gl_FragColor = vec4(col * a, a); /* premultiplied */
  }
`;

export class AboutSky {
  readonly canvas: HTMLCanvasElement;

  private readonly log = createLogger('about-sky');
  private gl: WebGLRenderingContext | null = null;
  private uTime: WebGLUniformLocation | null = null;
  private uRes: WebGLUniformLocation | null = null;
  private raf: number | null = null;
  private startMs = 0;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'ui-about__sky';
    this.canvas.setAttribute('aria-hidden', 'true');

    const gl = this.canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      depth: false,
      stencil: false,
    });
    if (!gl) {
      this.log.warn('WebGL unavailable for the About sky — backdrop stays plain blur.');
      return;
    }
    this.gl = gl;

    const compile = (type: number, src: string): WebGLShader | null => {
      const sh = gl.createShader(type);
      if (!sh) return null;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        this.log.warn('About-sky shader failed to compile:', gl.getShaderInfoLog(sh));
        return null;
      }
      return sh;
    };
    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = compile(gl.FRAGMENT_SHADER, FRAG);
    const prog = vs && fs ? gl.createProgram() : null;
    if (!prog || !vs || !fs) {
      this.gl = null;
      return;
    }
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.useProgram(prog);

    // One fullscreen triangle — fewer helpers than a quad, no index buffer.
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    this.uTime = gl.getUniformLocation(prog, 'uTime');
    this.uRes = gl.getUniformLocation(prog, 'uRes');

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }

  /** Begin rendering (About opened). Safe if WebGL was unavailable — it just no-ops. */
  start(): void {
    if (!this.gl || this.raf !== null) return;
    this.startMs = performance.now();
    this.resize();
    const frame = (): void => {
      this.render();
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  /** Stop rendering (About closed) — the loop only ever runs while the overlay is up. */
  stop(): void {
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  }

  private resize(): void {
    // Half-resolution backing store: the veils are soft by design, and this quarters the
    // fragment cost of a fullscreen 4-octave fbm.
    const w = Math.max(2, Math.round(this.canvas.clientWidth * 0.5));
    const h = Math.max(2, Math.round(this.canvas.clientHeight * 0.5));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  private render(): void {
    const gl = this.gl;
    if (!gl) return;
    this.resize();
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform1f(this.uTime, (performance.now() - this.startMs) / 1000);
    gl.uniform2f(this.uRes, this.canvas.width, this.canvas.height);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  dispose(): void {
    this.stop();
    this.gl?.getExtension('WEBGL_lose_context')?.loseContext();
    this.canvas.remove();
  }
}
